import GradientMask from '@atoms/GradientMask';
import RichText from '@atoms/RichText/RichText';
import { getCompanyLogo, getCompanyName } from '@constants/companies';
import { DEFAULT_FOCUS_POINT, FOCUS_POINT } from '@constants/constants';
import CtaButton from '@molecules/CtaButton/CtaButton';
import { CompanyHero as CompanyHeroType } from '@type-declarations/hero';
import useHeroAnimation from '@utils/animations/useHeroAnimation';
import clsx from 'clsx';
import Image from 'next/image';
import { useRef } from 'react';

import styles from './CompanyHero.module.scss';

interface Props extends Omit<CompanyHeroType, 'typename'> {}

export default function CompanyHero({
  company,
  title,
  text,
  image,
  focusPoint,
  cta,
  secondCta,
}: Props) {
  const logo = getCompanyLogo(company);
  const companyName = getCompanyName(company);

  const mediaRef = useRef<HTMLDivElement>(null!);

  useHeroAnimation(mediaRef, styles.mediaAnimation);

  return (
    <div className={styles.hero}>
      <div className={clsx('u-container', styles.columns)}>
        <div className={clsx(styles.firstColumn, text && styles.narrow)}>
          {logo ? (
            <div className={styles.logo}>
              <Image
                src={logo}
                alt={companyName || company}
                height={40}
                width={0}
                style={{
                  height: '2.5rem',
                  width: 'auto',
                }}
              />
            </div>
          ) : (
            <h2 className={styles.company}>{companyName}</h2>
          )}

          <h1 className="u-mb-0">{title}</h1>
        </div>

        {text && (
          <div className={styles.secondColumn}>
            {text && <RichText text={text} className={styles.text} />}

            {(cta || secondCta) && (
              <div className={styles.ctaWrapper}>
                {cta && <CtaButton cta={cta} />}
                {secondCta && <CtaButton cta={secondCta} variant="tertiary" />}
              </div>
            )}
          </div>
        )}
      </div>

      {image && (
        <div className={styles.mediaTransition} ref={mediaRef}>
          <div className={styles.mediaContainer}>
            <Image
              src={image.url}
              alt={image.description || ''}
              layout="fill"
              objectFit="cover"
              objectPosition={FOCUS_POINT[focusPoint || DEFAULT_FOCUS_POINT]}
              priority
              className={styles.media}
              quality={100}
            />

            <GradientMask />
          </div>
        </div>
      )}
    </div>
  );
}
